import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import useUtilities from "../../data/useUtilities";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { ContactType } from "../../models/Contact";
import { IVehicle } from "../../models/Vehicle";
import getAddress from "../../utils/getAddress";
import classNames from "../../utils/classNames";
import useNotification from "../notifications/useNotifications";
import Card from "../shared/Card/Card";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";
import VehicleCollectionQuestions from "./VehicleCollectionQuestions";

type FormValues = {
  fullname: string;
  contactType: ContactType;
  telephone1: string;
  telephone2: string;
  email: string;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  locality: string | null;
  town: string | null;
  county: string | null;
  postcode: string | null;
};

const emptyForm: FormValues = {
  fullname: "",
  contactType: ContactType.Customer,
  telephone1: "",
  telephone2: "",
  email: "",
  line1: "",
  line2: null,
  line3: null,
  line4: null,
  locality: null,
  town: null,
  county: null,
  postcode: null,
};

function getFormValuesFromVehicle(v: IVehicle): FormValues {
  return {
    fullname: "",
    contactType: ContactType.Customer,
    telephone1: "",
    telephone2: "",
    email: "",
    line1: v.line1 ? v.line1.trim() : "",
    line2: v.line2,
    line3: v.line3,
    line4: v.line4,
    locality: v.locality,
    town: v.town,
    county: v.county,
    postcode: v.postcode,
  };
}

export default function VehicleLocation({
  vehicle,
  variant = "standalone",
}: {
  vehicle: IVehicle | null;
  variant?: "standalone" | "embedded";
}) {
  const { update } = useVehiclesMutations();
  const { getAddresses } = useUtilities();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle location details");
  const { addNotification } = useNotification();
  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressesOpen, setAddressesOpen] = useState(false);
  const { register, getValues, setValue, watch, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: vehicle ? getFormValuesFromVehicle(vehicle) : emptyForm,
  });

  const onSubmit = (data: FormValues) => {
    if (!vehicle) return;
    const updatedVehicle = {
      ...vehicle,
      ...data,
    };

    update.mutate(updatedVehicle, saveHandlers);

    reset({
      fullname: data.fullname,
      contactType: data.contactType,
      telephone1: data.telephone1,
      telephone2: data.telephone2,
      email: data.email,
      line1: data.line1,
      line2: data.line2,
      line3: data.line3,
      line4: data.line4,
      locality: data.locality,
      town: data.town,
      county: data.county,
      postcode: data.postcode,
    });
  };

  const handlePostcode = () => {
    if (!vehicle) return;
    const postcode = getValues("postcode");

    getAddresses.mutate(postcode!, {
      onSuccess: (data: any) => {
        console.log(data);
        if (data.data.length !== 0) {
          setAddresses(
            data.data.sort((a: any, b: any) => {
              return a.line1 ? a.line1.localeCompare(b.line1, "en", { numeric: true }) : +1;
            })
          );
          setAddressesOpen(true);
        } else {
          addNotification({
            variant: "error",
            primaryText: "No addresses found",
          });
        }
      },
      onError: (error: unknown) => {
        console.error("error", error);
        addNotification({
          variant: "error",
          primaryText: "Could not get addresses",
        });
      },
    });
  };

  useEffect(() => {
    if (vehicle) {
      reset(getFormValuesFromVehicle(vehicle));
    } else {
      reset(emptyForm);
    }
  }, [vehicle?.id, vehicle?.updated, reset]);

  const line1 = watch("line1");

  const addressForm = (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
      <div className="grid grid-cols-1 gap-1">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <TextInput label="Postcode" register={register} registerName="postcode" rounded />
          </div>
          <ButtonInput
            classes="shrink-0 sm:mb-0"
            label="Get Addresses"
            isSubmit={false}
            onClick={() => handlePostcode()}
          />
        </div>
        <div>
          <TextInput
            label="Address Line 1"
            register={register}
            registerName="line1"
            customClasses={line1 ? "" : "text-red-600 border-red-600"}
            rounded
          />
          {!line1 && <p className="pt-2 text-sm text-red-500">Please enter an address line 1</p>}
        </div>
        <TextInput label="Address Line 2" register={register} registerName="line2" rounded />
        <TextInput label="Address Line 3" register={register} registerName="line3" rounded />
        <TextInput label="Address Line 4" register={register} registerName="line4" rounded />
        <TextInput label="Locality" register={register} registerName="locality" rounded />
        <TextInput label="Town" register={register} registerName="town" rounded />
        <TextInput label="County" register={register} registerName="county" rounded />
      </div>

      <div>
        <dt className="text-sm font-medium text-gray-500">Full Address</dt>
        <dd className="mt-1 text-sm text-gray-900">{vehicle ? getAddress(vehicle) : ""}</dd>
      </div>

      <ButtonInput classes={null} label="Update" isSubmit onClick={null} />
    </form>
  );

  const inner = (
    <>
      {variant === "embedded" && (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Vehicle pickup</h3>
      )}

      {!vehicle ? (
        <p className="text-sm text-gray-600">No vehicle is linked to this lead.</p>
      ) : (
        <>
          {addressForm}
          <VehicleCollectionQuestions vehicle={vehicle} />
        </>
      )}
    </>
  );

  const embeddedShell = (
    <div
      className={classNames(
        "min-w-0 space-y-2 rounded-lg",
        vehicle && !line1 && "bg-red-50"
      )}
    >
      {inner}
    </div>
  );

  const standaloneShell = (
    <Card
      title="Vehicle Pickup Details"
      bodyClassName={classNames("p-4 space-y-6", vehicle && !line1 && "bg-red-100")}
      collapsible
    >
      {inner}
    </Card>
  );

  return (
    <>
      <Modal open={addressesOpen} setOpen={setAddressesOpen} width="max-w-4xl">
        <div className="flex max-h-[80vh] w-full flex-col overflow-auto">
          {addresses.length > 0 &&
            addresses.map((address: any) => (
              <div
                key={address.id}
                className="flex w-full cursor-pointer flex-col border-b border-gray-200 p-4 hover:bg-gray-100"
                onClick={() => {
                  setValue("line1", address.line1);
                  setValue("line2", address.line2);
                  setValue("line3", address.line3);
                  setValue("line4", address.line4);
                  setValue("locality", address.locality);
                  setValue("town", address.town);
                  setValue("county", address.county);
                  setValue("postcode", address.postcode);
                  setAddressesOpen(false);
                }}
              >
                {getAddress(address)}
              </div>
            ))}
        </div>
      </Modal>

      {variant === "embedded" ? embeddedShell : standaloneShell}
    </>
  );
}
